import { Clock, Mail, MapPin, Phone } from "lucide-react";
import toast from "react-hot-toast";
import type { IconType } from "react-icons";
import { FaApple, FaFacebookF, FaGooglePlay, FaInstagram, FaWhatsapp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { NAV_LINKS, SITE } from "../../data/site";
import { StatusLoja } from "../ui/StatusLoja";
import { Logo } from "./Logo";

// Ícones de marca vêm do react-icons (o lucide não tem logos de empresas).
const SOCIAL: Array<{ label: string; icon: IconType }> = [
  { label: "Instagram", icon: FaInstagram },
  { label: "Facebook", icon: FaFacebookF },
  { label: "WhatsApp", icon: FaWhatsapp },
];

const STORES: Array<{ label: string; caption: string; icon: IconType }> = [
  { label: "App Store", caption: "Baixe na", icon: FaApple },
  { label: "Google Play", caption: "Disponível no", icon: FaGooglePlay },
];

const ColumnTitle = ({ children }: { children: string }) => (
  <h2 className="mb-5 font-display text-2xl tracking-wide text-white">{children}</h2>
);

export const Footer = () => (
  <footer className="relative border-t border-line bg-[#070707]">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr] lg:px-8">
      <div>
        <Logo />
        <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted">
          Blend suculento, molhos artesanais e a rapidez de um mestre das sombras.
        </p>
        <p className="mt-5 -rotate-2 font-script text-2xl text-primary">
          O hambúrguer que ataca sua fome
        </p>

        <div className="mt-6 flex gap-3">
          {SOCIAL.map(({ label, icon: Icon }) => (
            <button
              key={label}
              type="button"
              onClick={() => toast(`Link do ${label} ainda não disponível`)}
              aria-label={label}
              className="grid size-10 place-items-center rounded-full border border-line text-white transition-colors hover:border-primary hover:bg-primary hover:text-night"
            >
              <Icon aria-hidden="true" />
            </button>
          ))}
        </div>
      </div>

      <nav aria-label="Links rápidos">
        <ColumnTitle>Links Rápidos</ColumnTitle>
        <ul className="space-y-3 text-sm">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className="text-muted transition-colors hover:text-primary">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div>
        <ColumnTitle>Atendimento</ColumnTitle>
        <ul className="space-y-3 text-sm">
          {SITE.hours.map((h) => (
            <li key={h.days} className="flex items-start gap-3 text-muted">
              <Clock className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
              <span>
                <span className="text-white">{h.days}:</span> {h.time}
              </span>
            </li>
          ))}
        </ul>
        <StatusLoja className="mt-5" />
      </div>

      <div>
        <ColumnTitle>Contato</ColumnTitle>
        <ul className="space-y-3 text-sm text-muted">
          <li className="flex items-start gap-3">
            <MapPin className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
            <span>
              {SITE.address.street} — {SITE.address.city}
            </span>
          </li>
          <li className="flex items-center gap-3">
            <Phone className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <a href={`tel:+${SITE.whatsappNumber}`} className="hover:text-primary">
              {SITE.phone}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <Mail className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <a href={`mailto:${SITE.email}`} className="break-all hover:text-primary">
              {SITE.email}
            </a>
          </li>
        </ul>

        <div className="mt-6 flex flex-wrap gap-3">
          {STORES.map(({ label, caption, icon: Icon }) => (
            <button
              key={label}
              type="button"
              onClick={() => toast("Nosso app ainda não está disponível")}
              className="flex items-center gap-2.5 rounded-xl border border-line bg-card px-3.5 py-2 text-left transition-colors hover:border-primary"
            >
              <Icon className="size-5 text-white" aria-hidden="true" />
              <span className="leading-tight">
                <span className="block text-[10px] text-muted">{caption}</span>
                <span className="block text-sm font-semibold text-white">{label}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>

    <div className="border-t border-line">
      <p className="mx-auto max-w-7xl px-4 py-6 text-center text-xs text-muted sm:px-6 lg:px-8">
        &copy; {new Date().getFullYear()} {SITE.name}. Todos os direitos reservados.
      </p>
    </div>
  </footer>
);
